import React from 'react';
import { Link } from 'react-router-dom';
import './PrinciplesPage.css';

const identity = [
  { name: 'Partnership',  body: 'We figure it out with you. Your people, your language, your decisions. We contribute the way of seeing, and you keep the authority.' },
  { name: 'Independence', body: 'We build clients who can build themselves. The goal of every engagement is to be needed less, then not at all.' },
  { name: 'Curiosity',    body: 'The best insights come from discovery, not from proving how smart we are. We ask more than we answer, for longer than is comfortable.' },
  { name: 'Focus',        body: 'We stay with the thing that matters most and protect it from the drift toward adjacent work. Momentum comes from narrowness.' },
  { name: 'Proof',        body: 'Wins must be real. Measurable, sustained, visible in the bottom line. We verify every claim before we celebrate it.' },
];

const operating = [
  { name: 'Fix the system, not the symptom',          body: 'We only treat symptoms when doing so serves the systemic solution. Getting pulled into ops is the most common way a consultant becomes useless.' },
  { name: 'A system works exactly as it is designed', body: 'If the outcome is bad, the design is wrong. Blaming the people, the market, or the moment is how organisations stay stuck.' },
  { name: 'Business-matched pace',                    body: 'The business sets the timeline, within reason. Accelerated work needs closer collaboration, not shortcuts.' },
  { name: 'Professional honesty',                     body: 'We share what you need to know, when you are ready to hear it, with respect for your situation. Polite evasion does not build capable teams.' },
  { name: 'Quality without shortcuts',                body: 'We adapt our style to your context, but the work gets done properly. Half-finished systems do not last and do not transfer.' },
  { name: 'Wins must be real',                        body: 'Measurable. Bottom-line. Cash-in-bank. Anything else is just a presentation.' },
];

const CreedRow = ({ index, name, body }) => (
  <div className="creed__row">
    <div className="creed__num">{String(index + 1).padStart(2, '0')}</div>
    <div className="creed__name">{name}</div>
    <p className="creed__body">{body}</p>
  </div>
);

const PrinciplesPage = () => {
  return (
    <div className="principles-page">
      {/* ===== HERO ===== */}
      <section className="ip-hero ip-hero--quiet">
        <div className="ip-hero__texture" aria-hidden="true"></div>
        <div className="frame">
          <div className="ip-hero__inner">
            <div className="eyebrow-rule">I · Principles</div>
            <div className="ip-hero__wordmark">RussellStone Lighthouse</div>
            <h1 className="ip-hero__title">What we <em>are</em>. What we are <em>held</em> to.</h1>
            <p className="ip-hero__subcap">And what its consultants are held to.</p>
            <p className="ip-hero__lede">
              We separate two things most firms blend into one. Identity principles describe what we strive to be as a company. Operating principles describe what our consultants are held to in the room with you. The distinction matters because only one of them can get someone removed from an engagement.
            </p>
          </div>
        </div>
        <div className="ip-hero__horizon" aria-hidden="true"></div>
      </section>

      {/* ===== IDENTITY ===== */}
      <section className="section band--paper stitch--top">
        <div className="frame">
          <div className="sidehead">
            <aside className="sidehead__label">
              <div className="sidehead__roman">II</div>
              <div className="sidehead__cat">Identity · five principles</div>
            </aside>
            <div>
              <h2 className="sidehead__title">Five identity principles.</h2>
              <p className="sidehead__lede">
                What the firm aims to be, in the work and outside it. These are aspirations we judge ourselves against, not the bar that keeps a consultant on the engagement. The next chapter handles that.
              </p>

              <div className="creed">
                {identity.map((p, i) => (
                  <CreedRow key={p.name} index={i} name={p.name} body={p.body} />
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== TRANSITION SLAB ===== */}
      <section className="creed-bridge">
        <div className="stage">
          <div className="creed-bridge__rule" aria-hidden="true"></div>
          <p className="creed-bridge__pull">
            Identity is what we promise ourselves. The next six are what we promise <em>you</em>.
          </p>
        </div>
      </section>

      {/* ===== OPERATING ===== */}
      <section className="section band--paper stitch--top">
        <div className="frame">
          <div className="sidehead">
            <aside className="sidehead__label">
              <div className="sidehead__roman">III</div>
              <div className="sidehead__cat">Operating · six principles</div>
            </aside>
            <div>
              <h2 className="sidehead__title">Six operating principles.</h2>
              <p className="creed__lede">
                These are not aspirations. Consistent violation of any one of the six is cause for removal from a client. That is the line that makes the rest of them real.
              </p>

              <div className="creed">
                {operating.map((p, i) => (
                  <CreedRow key={p.name} index={i} name={p.name} body={p.body} />
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== PRACTICE CTA ===== */}
      <section className="final-cta">
        <div className="final-cta__lines"></div>
        <div className="stage">
          <div className="eyebrow-rule">IV · Practice</div>
          <h2 className="final-cta__title">
            Principles are abstract until they meet a business.
          </h2>
          <p className="final-cta__lede">
            The approach is the mechanism through which each of these shows up in the work. The engagement is where you see whether we live them.
          </p>
          <div className="final-cta__row">
            <Link to="/approach" className="btn btn--primary">
              Our approach <span className="arrow"></span>
            </Link>
            <Link to="/contact" className="btn btn--ghost">
              Start a conversation
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PrinciplesPage;
